import { useEffect, useState } from 'react';
import { API_BASE } from '../config';
import SeoHead from './SeoHead';
import { pageMeta } from '../seoUtils';
import './AdvertisePage.css';

const TIER_OPTIONS = [
  { value: 'minimum', label: 'Minimum' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'full', label: 'Full' },
];

const DURATION_PRESETS = [7, 14, 30];

const TERMS_TEXT = [
  'You are solely responsible for the accuracy, legality, and content of your ad, including any claims, images, and the linked website.',
  'NaijaNest is not a party to, and accepts no liability for, any transaction, dispute, or communication between you and anyone who views or responds to your ad.',
  'Submitting this form does not guarantee approval. We may reject, edit, or remove any ad at our sole discretion, at any time, including after payment, without obligation to explain.',
  'Fees are non-refundable once an ad has been approved and goes live. If we reject your ad before approval, no charge applies.',
  'We do not guarantee any specific number of impressions, clicks, or results.',
  'You agree to indemnify NaijaNest against any claim, loss, or damage arising from your ad or the products/services it promotes.',
];

function formatNaira(amount) {
  return `₦${Number(amount || 0).toLocaleString()}`;
}

function emptyForm() {
  return {
    businessName: '',
    contactName: '',
    email: '',
    contactPhone: '',
    businessCategory: '',
    clickUrl: '',
    imageUrl: '',
    description: '',
    advertiserNotes: '',
    displayTier: 'minimum',
    durationDays: 30,
    useCustomDuration: true,
    customDuration: '1',
    acceptedTerms: false,
  };
}

function AdvertisePage({ onNavigate }) {
  const [pricing, setPricing] = useState({ minimum: 2000, moderate: 3500, full: 6000 });
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/ads/pricing`)
      .then((res) => res.json())
      .then((data) => setPricing(data))
      .catch(() => {
        // Keep the built-in defaults.
      });
  }, []);

  const days = form.useCustomDuration
    ? Math.max(0, Number(form.customDuration) || 0)
    : form.durationDays;
  const rate = pricing[form.displayTier] || 0;
  const total = rate * days;

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleImageSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {return;}

    setImageUploadError('');
    setImageUploading(true);
    try {
      const body = new FormData();
      body.append('image', file);
      const res = await fetch(`${API_BASE}/ads/requests/image`, {
        method: 'POST',
        body,
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to upload image.');
      }
      setField('imageUrl', data.thumbnailUrl);
    } catch (err) {
      setImageUploadError(err.message);
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (!form.imageUrl) {
      setError('Please upload a banner image.');
      return;
    }

    if (days < 1 || days > 90) {
      setError('Duration must be between 1 and 90 days.');
      return;
    }

    if (!form.acceptedTerms) {
      setError('Please accept the Advertising Terms & Conditions to continue.');
      return;
    }

    setSubmitting(true);
    try {
      const referralCode = new URLSearchParams(window.location.search).get('ref') || undefined;
      const res = await fetch(`${API_BASE}/ads/requests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          businessName: form.businessName.trim(),
          contactName: form.contactName.trim(),
          email: form.email.trim(),
          contactPhone: form.contactPhone.trim(),
          businessCategory: form.businessCategory.trim() || undefined,
          clickUrl: form.clickUrl.trim(),
          imageUrl: form.imageUrl.trim(),
          description: form.description.trim() || undefined,
          advertiserNotes: form.advertiserNotes.trim() || undefined,
          displayTier: form.displayTier,
          durationDays: days,
          referralCode,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to submit your ad request.');
      }
      if (data.authorizationUrl) {
        // Off to Paystack's hosted checkout - it redirects back to
        // /advertise/callback once payment completes, so nothing more to do
        // here (this component unmounts on navigation).
        window.location.href = data.authorizationUrl;
        return;
      }
      // Paystack init failed server-side (ad row was still saved) - fall
      // back to the manual-payment confirmation screen.
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const seo = pageMeta({
    title: 'Advertise on NaijaNest',
    description:
      'Put your business in front of thousands of property buyers and sellers across Nigeria.',
    path: '/advertise',
  });

  if (result) {
    return (
      <main className="auth-page">
        <SeoHead
          title={seo.fullTitle}
          description={seo.desc}
          url={seo.url}
          image={seo.img}
          noIndex={seo.noIndex}
        />
        <div className="auth-card">
          <div className="auth-card__header">
            <h1 className="auth-card__title">Request submitted</h1>
            <p className="auth-card__subtitle">{result.message}</p>
          </div>
          <p className="advertise-confirmation__total">
            Total: <strong>{formatNaira(result.paymentAmount)}</strong>
          </p>
          <p className="advertise-confirmation__note">
            Our team will reach out with payment details before your ad goes live.
          </p>
          <button type="button" className="auth-form__submit" onClick={() => onNavigate('/')}>
            Back to home
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="auth-page advertise-page">
      <SeoHead
        title={seo.fullTitle}
        description={seo.desc}
        url={seo.url}
        image={seo.img}
        noIndex={seo.noIndex}
      />
      <div className="auth-card advertise-card">
        <div className="auth-card__header advertise-card__header">
          <h1 className="auth-card__title">Advertise on NaijaNest</h1>
          <p className="auth-card__subtitle">
            Reach thousands of property buyers and sellers across Nigeria.
          </p>
        </div>

        <form className="advertise-grid" onSubmit={handleSubmit} noValidate>
          {error && (
            <div className="auth-form__error advertise-grid__error" role="alert">
              {error}
            </div>
          )}

          <div className="advertise-col">
            <h2 className="advertise-col__title">Your business</h2>

            <div className="advertise-form__row">
              <div className="auth-form__field">
                <label htmlFor="ad-business-name" className="auth-form__label">
                  Business name *
                </label>
                <input
                  id="ad-business-name"
                  type="text"
                  className="auth-form__input"
                  value={form.businessName}
                  onChange={(e) => setField('businessName', e.target.value)}
                  required
                />
              </div>
              <div className="auth-form__field">
                <label htmlFor="ad-contact-name" className="auth-form__label">
                  Contact person *
                </label>
                <input
                  id="ad-contact-name"
                  type="text"
                  className="auth-form__input"
                  value={form.contactName}
                  onChange={(e) => setField('contactName', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="advertise-form__row">
              <div className="auth-form__field">
                <label htmlFor="ad-email" className="auth-form__label">
                  Email *
                </label>
                <input
                  id="ad-email"
                  type="email"
                  className="auth-form__input"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  required
                />
              </div>
              <div className="auth-form__field">
                <label htmlFor="ad-phone" className="auth-form__label">
                  Phone / WhatsApp *
                </label>
                <input
                  id="ad-phone"
                  type="tel"
                  className="auth-form__input"
                  value={form.contactPhone}
                  onChange={(e) => setField('contactPhone', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="advertise-form__row">
              <div className="auth-form__field">
                <label htmlFor="ad-category" className="auth-form__label">
                  Business category
                </label>
                <input
                  id="ad-category"
                  type="text"
                  className="auth-form__input"
                  placeholder="e.g. Home & Interiors"
                  value={form.businessCategory}
                  onChange={(e) => setField('businessCategory', e.target.value)}
                />
              </div>
              <div className="auth-form__field">
                <label htmlFor="ad-click-url" className="auth-form__label">
                  Website *
                </label>
                <input
                  id="ad-click-url"
                  type="url"
                  className="auth-form__input"
                  placeholder="https://"
                  value={form.clickUrl}
                  onChange={(e) => setField('clickUrl', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="auth-form__field">
              <label htmlFor="ad-tagline" className="auth-form__label">
                Tagline ({form.description.length}/80)
              </label>
              <input
                id="ad-tagline"
                type="text"
                className="auth-form__input"
                maxLength={80}
                placeholder="New showroom now open in Lekki"
                value={form.description}
                onChange={(e) => setField('description', e.target.value)}
              />
            </div>

            <div className="auth-form__field">
              <label htmlFor="ad-notes" className="auth-form__label">
                Anything else? ({form.advertiserNotes.length}/500)
              </label>
              <textarea
                id="ad-notes"
                className="auth-form__input advertise-form__textarea"
                rows={2}
                maxLength={500}
                value={form.advertiserNotes}
                onChange={(e) => setField('advertiserNotes', e.target.value)}
              />
            </div>
          </div>

          <div className="advertise-col">
            <h2 className="advertise-col__title">Your ad</h2>

            <div className="auth-form__field">
              <label htmlFor="ad-image-upload" className="auth-form__label">
                Banner image *
              </label>
              <div className="advertise-form__image-row">
                <input
                  id="ad-image-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="auth-form__input"
                  onChange={handleImageSelect}
                />
                {form.imageUrl && !imageUploading && (
                  <img
                    className="advertise-form__image-thumb"
                    src={form.imageUrl}
                    alt="Ad banner preview"
                  />
                )}
              </div>
              {imageUploading && <p className="advertise-form__image-status">Uploading…</p>}
              {imageUploadError && (
                <p className="advertise-form__image-status advertise-form__image-status--error">
                  {imageUploadError}
                </p>
              )}
            </div>

            <div className="auth-form__field">
              <span className="auth-form__label">Tier *</span>
              <div className="advertise-form__tier-row">
                {TIER_OPTIONS.map((opt) => (
                  <label
                    key={opt.value}
                    className={`advertise-tier-option${
                      form.displayTier === opt.value ? ' advertise-tier-option--selected' : ''
                    }`}
                  >
                    <input
                      type="radio"
                      name="tier"
                      checked={form.displayTier === opt.value}
                      onChange={() => setField('displayTier', opt.value)}
                    />
                    <span className="advertise-tier-option__label">{opt.label}</span>
                    <span className="advertise-tier-option__rate">
                      {formatNaira(pricing[opt.value])}/day
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="auth-form__field">
              <span className="auth-form__label">Duration *</span>
              <div className="advertise-form__duration-row">
                {DURATION_PRESETS.map((preset) => (
                  <label key={preset} className="advertise-duration-chip">
                    <input
                      type="radio"
                      name="duration"
                      checked={!form.useCustomDuration && form.durationDays === preset}
                      onChange={() =>
                        setForm((f) => ({ ...f, durationDays: preset, useCustomDuration: false }))
                      }
                    />
                    {preset}d
                  </label>
                ))}
                <label className="advertise-duration-chip">
                  <input
                    type="radio"
                    name="duration"
                    checked={form.useCustomDuration}
                    onChange={() => setField('useCustomDuration', true)}
                  />
                  <input
                    type="number"
                    min={1}
                    max={90}
                    className="advertise-duration-chip__input"
                    value={form.customDuration}
                    onFocus={() => setField('useCustomDuration', true)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        customDuration: e.target.value,
                        useCustomDuration: true,
                      }))
                    }
                  />
                  d
                </label>
                <span className="advertise-form__total">
                  {days}d × {formatNaira(rate)} = <strong>{formatNaira(total)}</strong>
                </span>
              </div>
            </div>

            <div className="advertise-terms">
              <p className="advertise-terms__heading">Advertising Terms &amp; Conditions</p>
              <ol className="advertise-terms__list">
                {TERMS_TEXT.map((term) => (
                  <li key={term}>{term}</li>
                ))}
              </ol>
              <label className="advertise-terms__accept">
                <input
                  type="checkbox"
                  checked={form.acceptedTerms}
                  onChange={(e) => setField('acceptedTerms', e.target.checked)}
                />
                I have read and agree to the Advertising Terms &amp; Conditions.
              </label>
            </div>

            <button
              type="submit"
              className="auth-form__submit"
              disabled={submitting || imageUploading || !form.acceptedTerms}
            >
              {submitting ? 'Submitting…' : `Submit & Pay ${formatNaira(total)}`}
            </button>
          </div>
        </form>
      </div>
    </main>
  );
}

export default AdvertisePage;
